import { Injectable, inject } from '@angular/core';

import { AppConfig } from '@frontend/sports/common/client-config-data-access';
import { CdsPushProvider, CdsPushService } from '@frontend/sports/content-distribution/feature';
import { NonRootToken } from '@frontend/sports/host-app/sports-product/feature/non-root-token';
import { SportsProductUtils } from '@frontend/sports/host-app/sports-product/feature/utils';
import { TrackingService, trackingConstants } from '@frontend/sports/tracking/feature';
import { SportsClientConfigRefreshService, UserService } from '@frontend/sports/user/feature';
import { DeviceService, NativeAppService } from '@frontend/vanilla/core';
import { PageVisibilityService } from '@frontend/vanilla/features/page-visibility';
import { Subject, filter, merge, throttleTime } from 'rxjs';

import { FreshDataProvider, FreshDataType } from './fresh-data-provider.service';
import { OnlineDetectionService } from './online-detection.service';

@Injectable({ providedIn: 'root' })
export class FreshDataService {
    private pushProvider = inject(CdsPushProvider);
    private appConfig = inject(AppConfig);
    private ms2Tracking = inject(TrackingService);
    private cdsPushService = inject(CdsPushService);

    private reloadNeededStream = new Subject<FreshDataType>();
    private isOnline = false;
    private isVisible = true;

    constructor() {
        const pageVisibilityService = inject(PageVisibilityService);
        const onlineDetection = inject(OnlineDetectionService);
        const deviceService = inject(DeviceService);
        const nativeApp = inject(NativeAppService);
        const userService = inject(UserService);
        const freshDataProviders: FreshDataProvider[] = inject(NonRootToken(FreshDataProvider));
        const clientConfigRefresh = inject(SportsClientConfigRefreshService);
        const productUtils = inject(SportsProductUtils);

        pageVisibilityService
            .visibilityChange()
            .pipe(
                filter(() => deviceService.isMobile),
                productUtils.filterSportsEmitLast(),
            )
            .subscribe((vis) => this.visibilityChange(vis));

        onlineDetection
            .onlineChange()
            .pipe(productUtils.filterSportsEmitLast())
            .subscribe((online) => this.onlineChange(online));

        nativeApp.eventsFromNative
            .pipe(
                filter((e) => e.eventName === 'APP_FOREGRND'),
                productUtils.filterSportsEmitLast(),
            )
            .subscribe(() => this.requestReload(FreshDataType.DataAndPush));

        merge(...freshDataProviders.map((_) => _.isRefreshNeeded()))
            .pipe(productUtils.filterSportsEmitLast())
            .subscribe((type) => this.requestReload(type));

        merge(userService.onAuthenticationChange$, clientConfigRefresh.locationDependantDataNeeded$)
            .pipe(productUtils.filterSportsEmitLast())
            .subscribe(() => {
                this.requestReload(FreshDataType.Data);
            });
    }

    readonly reloadNeeded = this.reloadNeededStream.pipe(throttleTime(150));

    private visibilityChange = ({ isVisible, pageHiddenDuration }: { isVisible: boolean; pageHiddenDuration?: number }) => {
        this.isVisible = isVisible;
        if (!this.isVisible) {
            return;
        }

        const pageHiddenForTooLong =
            (pageHiddenDuration || 0) > this.appConfig.pageHiddenReloadThreshold && this.appConfig.pageHiddenReloadThreshold > 0;

        if (this.isOnline && (this.pushProvider.isDisconnected || pageHiddenForTooLong)) {
            if (pageHiddenForTooLong) {
                this.ms2Tracking.track(trackingConstants.EVENT_RELOAD_HIDDEN, {
                    [trackingConstants.PAGE_RELOAD_AFTER_HIDDEN]: pageHiddenDuration,
                });
            }
            this.requestReload(FreshDataType.DataAndPush);
        }
    };

    private onlineChange = ({ isOnline, offlineDuration }: { isOnline: boolean; offlineDuration?: number }) => {
        this.isOnline = isOnline;

        if (!isOnline) {
            return;
        }

        const offlineForTooLong =
            (offlineDuration || 0) > this.appConfig.offlineDurationReloadThreshold && this.appConfig.offlineDurationReloadThreshold > 0;

        if (this.isVisible && (offlineForTooLong || this.pushProvider.isDisconnected)) {
            if (offlineForTooLong) {
                this.ms2Tracking.track(trackingConstants.EVENT_RELOAD_OFFLINE, {
                    [trackingConstants.PAGE_RELOAD_AFTER_OFFLINE]: offlineDuration,
                });
            }
            this.requestReload(FreshDataType.DataAndPush);
        }
    };

    private async requestReload(type: FreshDataType): Promise<void> {
        if (type === FreshDataType.DataAndPush) {
            await this.cdsPushService.restart();
        }

        this.reloadNeededStream.next(type);
    }
}
